import React, { useEffect, useMemo, useRef, useState } from 'react';
import EntityCombobox from '../components/EntityCombobox';
import type { ComboboxOption } from '../components/EntityCombobox';
import Alert from '../components/ui/Alert';
import { useExiting, useFlip } from '../components/ui/motion';
import Menu from '../components/ui/Menu';
import type { MenuItem } from '../components/ui/Menu';
import SegTrack from '../components/ui/SegTrack';
import Tooltip from '../components/ui/Tooltip';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { useToast } from '../components/Toast/ToastContext';
import { addMember, errorMessage, listActiveUsers, removeMember, setMemberRole, transferPlan } from './api';
import { ROW_BUTTON, TONAL_SMALL } from './DocSection';
import type { ActiveUser, LinkAccess, MemberRole, PlanMember, PlanPatch, PlanResponse } from './types';


const LINK_OPTIONS = [
  { key: 'members', label: 'Members only' },
  { key: 'anyone_signed_in', label: 'Anyone signed in' },
];

const ROLE_LABEL: Record<MemberRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };
const ROLE_ORDER: Record<MemberRole, number> = { owner: 0, admin: 1, member: 2 };

const fullName = (p: { firstname: string; lastname: string }) => `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Former user';

export interface SharePanelProps {
  planId: string;
  data: PlanResponse;
  userId: string | null;
  onPatch: (patch: PlanPatch, success?: string) => Promise<boolean>;
  reload: () => Promise<void>;
}

/**
 * Who has this plan and what they may do. The server enforces every rule; this draws
 * only the controls it would accept: the owner changes any role and removes anyone,
 * an admin promotes members and removes members, never another admin.
 */
const SharePanel: React.FC<SharePanelProps> = ({ planId, data, userId, onPatch, reload }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const { plan, role, members } = data;
  const isOwner = role === 'owner';
  const manager = role === 'owner' || role === 'admin';
  const [users, setUsers] = useState<ActiveUser[] | null>(null);
  const [usersFailed, setUsersFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (!manager) return undefined;
    let live = true;
    listActiveUsers()
      .then((list) => live && setUsers(list))
      .catch(() => live && setUsersFailed(true));
    return () => {
      live = false;
    };
  }, [manager]);

  const sorted = useMemo(
    () =>
      [...members].sort(
        (a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || (a.firstname || '').localeCompare(b.firstname || ''),
      ),
    [members],
  );

  const shownMembers = useExiting(sorted, (m) => m.user_id);
  const peopleList = useRef<HTMLUListElement>(null);
  useFlip(peopleList, [shownMembers.map((s) => (s.leaving ? `${s.key}~` : s.key)).join('|')]);

  const addOptions = useMemo<ComboboxOption[]>(() => {
    const taken = new Set(members.map((m) => m.user_id));
    return (users ?? []).filter((u) => !taken.has(u.id)).map((u) => ({ id: u.id, label: fullName(u) }));
  }, [users, members]);

  const transferOptions = useMemo<ComboboxOption[]>(
    () => sorted.filter((m) => m.role !== 'owner').map((m) => ({ id: m.user_id, label: fullName(m), chip: ROLE_LABEL[m.role] })),
    [sorted],
  );

  const run = async (fn: () => Promise<unknown>, success: string, failure: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
    } catch (e) {
      toast.error(errorMessage(e, failure));
    } finally {
      setBusy(false);
      reload();
    }
  };

  const add = (option: ComboboxOption | null) => {
    if (!option) return;
    run(() => addMember(planId, String(option.id)), `${option.label} can now open this plan.`, 'Could not add that person.');
  };

  const itemsFor = (m: PlanMember): MenuItem[] => {
    if (m.role === 'owner' || !manager) return [];
    if (!isOwner && m.role === 'admin') return [];
    const who = fullName(m);
    const items: MenuItem[] = [];
    if (m.role === 'member') {
      items.push({
        label: 'Make admin',
        hint: 'Can edit every topic and share the plan',
        onSelect: () => run(() => setMemberRole(planId, m.user_id, 'admin'), `${who} is now an admin.`, 'Could not change that role.'),
      });
    } else if (isOwner) {
      items.push({
        label: 'Make member',
        onSelect: () => run(() => setMemberRole(planId, m.user_id, 'member'), `${who} is now a member.`, 'Could not change that role.'),
      });
    }
    items.push({
      label: 'Remove from plan',
      onSelect: async () => {
        const ok = await confirm({
          title: `Remove ${who}?`,
          message: 'They lose access to this plan, and any topic they hold goes back to no one.',
          confirmLabel: 'Remove',
          tone: 'danger',
        });
        if (ok) run(() => removeMember(planId, m.user_id), `${who} was removed.`, 'Could not remove that person.');
      },
    });
    return items;
  };

  const transfer = async (option: ComboboxOption | null) => {
    if (!option) return;
    const ok = await confirm({
      title: `Hand this plan to ${option.label}?`,
      message: `${option.label} becomes the owner. You stay on the plan as an admin, and only the new owner can hand it back.`,
      confirmLabel: 'Transfer ownership',
      tone: 'danger',
    });
    if (!ok) return;
    setTransferring(false);
    run(() => transferPlan(planId, String(option.id)), `${option.label} now owns this plan.`, 'Could not transfer ownership.');
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/node-plans/${plan.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error(`Could not copy. The link is ${url}`);
    }
  };

  const linkNote =
    plan.link_access === 'anyone_signed_in'
      ? 'Anyone signed in can open the link and read. Only members can add points.'
      : 'Only the people listed here can open the link.';

  return (
    <div className="space-y-6">
      {manager && (
        <div>
          {usersFailed ? (
            <Alert variant="text" live={false}>Could not load the people list. Close this and try again.</Alert>
          ) : (
            <EntityCombobox
              id="np-share-add"
              label="Add people"
              value=""
              options={addOptions}
              onSelect={add}
              disabled={busy || users === null}
              placeholder={users === null ? 'Loading people' : 'Type a name, then Enter'}
              emptyMessage="Nobody by that name, or they are already here."
              noOptionsMessage="Everyone active already has this plan."
              openOnFocus={false}
            />
          )}
        </div>
      )}

      <section aria-labelledby="np-share-people">
        <h3 id="np-share-people" className="mb-1 text-xs font-medium text-on-surface-variant">
          <span className="tabular-nums">{members.length}</span> {members.length === 1 ? 'person' : 'people'} with access
        </h3>
        <ul ref={peopleList} className="-mx-2">
          {shownMembers.map(({ item: m, leaving }) => {
            const items = itemsFor(m);
            const me = m.user_id === userId;
            return (
              <li
                key={m.user_id}
                data-flip-key={m.user_id}
                aria-hidden={leaving || undefined}
                // React 17 does not type `inert`; it takes the leaving row's menu out of the tab order.
                {...(leaving ? ({ inert: '' } as object) : {})}
                className={`flex min-h-[44px] items-center gap-2 rounded-lg px-2 transition-opacity duration-exit ease-exit motion-reduce:transition-none ${
                  leaving ? 'pointer-events-none opacity-0' : 'opacity-100'
                }`}
              >
                <Tooltip label={fullName(m)} truncated>
                  <span className="min-w-0 flex-1 truncate text-sm text-on-surface">
                    {fullName(m)}
                    {me && <span className="ml-1 text-on-surface-variant">(you)</span>}
                  </span>
                </Tooltip>
                {items.length > 0 ? (
                  <Menu label={ROLE_LABEL[m.role]} items={items} />
                ) : (
                  <span
                    className={`flex-shrink-0 px-2.5 text-xs ${m.role === 'owner' ? 'font-medium text-on-surface' : 'text-on-surface-variant'}`}
                  >
                    {ROLE_LABEL[m.role]}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="np-share-link">
        <h3 id="np-share-link" className="mb-2 text-xs font-medium text-on-surface-variant">
          Who can open the link
        </h3>
        {manager ? (
          <SegTrack
            label="Who can open the link"
            options={LINK_OPTIONS}
            value={plan.link_access}
            onChange={(k) => {
              if (k !== plan.link_access) {
                onPatch(
                  { link_access: k as LinkAccess },
                  k === 'members' ? 'Only members can open the link now.' : 'Anyone signed in can open the link now.',
                );
              }
            }}
          />
        ) : (
          <p className="text-sm text-on-surface">{plan.link_access === 'members' ? 'Members only' : 'Anyone signed in'}</p>
        )}
        <p className="mt-2 text-xs text-on-surface-variant">{linkNote}</p>
        <Tooltip label="Copy this plan's address to the clipboard">
          <button type="button" onClick={copyLink} className={`${TONAL_SMALL} mt-2 gap-1.5`}>
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none">
              <path
                fill="currentColor"
                d="M10.6 13.4a1 1 0 0 1 0-1.4l3.5-3.5a3 3 0 1 1 4.2 4.2l-2 2-1.4-1.4 2-2a1 1 0 1 0-1.4-1.4L12 13.4a1 1 0 0 1-1.4 0zM13.4 10.6a1 1 0 0 1 0 1.4l-3.5 3.5a3 3 0 1 1-4.2-4.2l2-2 1.4 1.4-2 2a1 1 0 1 0 1.4 1.4L12 10.6a1 1 0 0 1 1.4 0z"
              />
            </svg>
            Copy link
          </button>
        </Tooltip>
      </section>

      {isOwner && transferOptions.length > 0 && (
        <section aria-label="Ownership" className="border-t border-outline-variant pt-3">
          {transferring ? (
            <div className="space-y-2">
              <EntityCombobox
                id="np-share-transfer-to"
                label="New owner"
                value=""
                options={transferOptions}
                onSelect={transfer}
                disabled={busy}
                placeholder="Pick a member"
                emptyMessage="Only a member of this plan can own it."
                autoFocus
              />
              <button type="button" onClick={() => setTransferring(false)} className={`${ROW_BUTTON} -ml-2.5`}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setTransferring(true)} disabled={busy} className={`${ROW_BUTTON} -ml-2.5`}>
              Transfer ownership
            </button>
          )}
        </section>
      )}
    </div>
  );
};

export default SharePanel;
